<template>
  <footer class="footer glass">
    <div class="container">
      <div class="footer-content">
        <div class="footer-section footer-about">
          <h3 class="footer-logo">{{ $t('footer.logo') }}</h3>
          <p class="footer-description">
            {{ $t('footer.description') }}
          </p>
          <div class="social-links">
            <a
              :href="link.url"
              class="social-link"
              v-for="(link, index) in socialLinks"
              :key="index"
              :aria-label="$t(link.ariaLabelKey)"
              @click="handleSocialLinkClick(link, $event)"
              rel="noopener noreferrer"
            >
              <img
                v-if="link.iconImage"
                class="social-image-icon"
                :src="link.iconImage"
                alt=""
                aria-hidden="true"
              >
              <span v-else v-html="link.icon"></span>
              <!-- 二维码弹出层 -->
              <div class="social-qrcode glass-sm" aria-hidden="true">
                <div class="qrcode-content">
                  <p class="qrcode-text">{{ $t(link.qrcodeTextKey) }}</p>
                  <div class="qrcode-image-wrapper">
                    <img class="qrcode-image" :src="link.qrcodeImage" alt="">
                    <span
                      v-if="link.centerLabel"
                      class="qrcode-center-label"
                      aria-hidden="true"
                    >
                      {{ link.centerLabel }}
                    </span>
                  </div>
                </div>
                <!-- 三角形指示器 -->
                <div class="qrcode-arrow"></div>
              </div>
            </a>
          </div>
        </div>
        <div class="footer-section footer-links-section">
          <h4>{{ $t('footer.services.title') }}</h4>
          <ul>
            <li v-for="(item, index) in $tm('footer.services.items')" :key="index">
              <router-link v-if="index === 0" to="/services/expert">{{ item }}</router-link>
              <router-link v-else-if="index === 1" to="/services/data">{{ item }}</router-link>
              <router-link v-else-if="index === 2" to="/services/roadshow">{{ item }}</router-link>
              <router-link v-else-if="index === 3" to="/services/sitevisit">{{ item }}</router-link>
              <router-link v-else-if="index === 4" to="/services/research">{{ item }}</router-link>
              <a v-else href="#">{{ item }}</a>
            </li>
          </ul>
        </div>
        <div class="footer-section footer-links-section">
          <h4>{{ $t('footer.about.title') }}</h4>
          <ul>
            <li v-for="(item, index) in $tm('footer.about.items')" :key="index">
              <router-link v-if="index === 0" to="/about/overview">{{ item }}</router-link>
              <router-link v-else-if="index === 1" to="/expert-introduction">{{ item }}</router-link>
              <router-link v-else-if="index === 2" to="/compliance-framework">{{ item }}</router-link>
              <a v-else-if="index === 3" href="#" @click.prevent="goToCareer">{{ item }}</a>
              <a v-else href="#">{{ item }}</a>
            </li>
          </ul>
        </div>
        <div class="footer-section footer-contact">
          <h4>{{ $t('footer.contact.title') }}</h4>
          <ul>
            <li>{{ $t('footer.contact.address1') }}</li>
            <li>{{ $t('footer.contact.address2') }}</li>
            <li>{{ $t('footer.contact.phone') }}</li>
            <li>{{ $t('footer.contact.email') }}</li>
          </ul>
        </div>
      </div>
      <div class="footer-bottom">
        <p>{{ $t('footer.copyright') }}</p>
        <div class="footer-links">
          <a href="#">{{ $t('footer.links.privacy') }}</a>
          <a href="#">{{ $t('footer.links.terms') }}</a>
          <a href="#">{{ $t('footer.links.sitemap') }}</a>
        </div>
      </div>
      <div class="footer-legal" :aria-label="$t('footer.legalAriaLabel')">
        <a
          v-if="$t('footer.psb')"
          :href="$t('footer.psbLink')"
          class="psb-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img class="psb-icon" src="/zgwab.png" alt="" aria-hidden="true">
          {{ $t('footer.psb') }}
        </a>
        <span v-if="$t('footer.psb') && $t('footer.icp')" class="legal-divider" aria-hidden="true"></span>
        <a v-if="$t('footer.icp')" :href="$t('footer.icpLink')" target="_blank" rel="noopener noreferrer">
          {{ $t('footer.icp') }}
        </a>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { linkedin } from '@/assets/icons'
import linkedinQrcode from '@/assets/linkedin-qrcode.png'
import wecomQrcode from '@/assets/wecom-qrcode.png'

const linkedinCompanyUrl = 'https://www.linkedin.com/company/gaoyi-consulting/'

const socialLinks = ref([
  {
    icon: linkedin,
    type: 'linkedin',
    ariaLabelKey: 'footer.social.linkedinAriaLabel',
    qrcodeTextKey: 'footer.social.linkedinQrcodeText',
    qrcodeImage: linkedinQrcode,
    centerLabel: 'in',
    url: linkedinCompanyUrl
  },
  {
    iconImage: '/wechat_enterprise.png',
    type: 'wework',
    ariaLabelKey: 'footer.social.weworkAriaLabel',
    qrcodeTextKey: 'footer.social.weworkQrcodeText',
    qrcodeImage: wecomQrcode,
    url: 'javascript:;'
  }
])

// 社交链接点击处理
const handleSocialLinkClick = (link, event) => {
  if (link.type === 'linkedin') {
    // 领英账号点击跳转外部链接
    window.open(link.url, '_blank', 'noopener noreferrer')
  }
  // 企业微信阻止默认跳转行为，仅hover展示二维码
  event.preventDefault()
}

const route = useRoute()
const router = useRouter()

const goToCareer = () => {
  if (route.path !== '/') {
    router.push('/').then(() => {
      setTimeout(() => {
        const careerSection = document.getElementById('career')
        if (careerSection) {
          careerSection.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    })
  } else {
    const careerSection = document.getElementById('career')
    if (careerSection) {
      careerSection.scrollIntoView({ behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.footer {
  background: color-mix(in srgb, var(--white) 70%, transparent);
  border-top: 1px solid color-mix(in srgb, var(--white) 60%, transparent);
  color: var(--deep-navy);
  padding: var(--spacing-xl) 0 var(--spacing-md);
  position: relative;
  box-shadow:
    0 4px 30px rgba(0, 0, 0, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, color-mix(in srgb, var(--primary-blue) 8%, transparent) 0%, color-mix(in srgb, var(--deep-navy) 5%, transparent) 50%, color-mix(in srgb, var(--primary-blue) 3%, transparent) 100%);
  pointer-events: none;
  z-index: -1;
}

@media (max-width: 1366px) {
  .footer {
    padding: 70px 0 25px;
  }
}

@media (max-width: 1024px) {
  .footer {
    padding: 60px 0 25px;
  }
}

@media (max-width: 768px) {
  .footer {
    padding: 50px 0 20px;
  }
}


.footer-content {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.5fr;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
}

@media (max-width: 1366px) {
  .footer-content {
    gap: var(--spacing-lg);
    margin-bottom: var(--spacing-lg);
  }
}

@media (max-width: 1024px) {
  .footer-content {
    grid-template-columns: 1fr 1fr;
    gap: var(--spacing-lg) var(--spacing-md);
    margin-bottom: var(--spacing-lg);
  }
}

@media (max-width: 768px) {
  .footer-content {
    grid-template-columns: 1fr;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-md);
  }
}

.footer-section h4 {
  font-size: 1.1rem;
  margin-bottom: 25px;
  font-weight: 600;
}

@media (max-width: 1366px) {
  .footer-section h4 {
    font-size: 1.05rem;
    margin-bottom: 20px;
  }
}

@media (max-width: 1024px) {
  .footer-section h4 {
    font-size: 1rem;
    margin-bottom: 18px;
  }
}

@media (max-width: 768px) {
  .footer-section h4 {
    font-size: 1rem;
    margin-bottom: 15px;
  }
}

.footer-section ul {
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
  padding: 0;
  margin: 0;
}

@media (max-width: 768px) {
  .footer-section ul {
    gap: 10px;
  }
}

.footer-section ul li,
.footer-section ul li a {
  color: rgba(15, 39, 64, 0.7);
  transition: color 0.3s ease;
  font-size: 0.95rem;
  line-height: 1.6;
}

@media (max-width: 1366px) {
  .footer-section ul li,
  .footer-section ul li a {
    font-size: 0.9rem;
  }
}

@media (max-width: 768px) {
  .footer-section ul li,
  .footer-section ul li a {
    font-size: 0.9rem;
  }
}

.footer-section ul li a:hover {
  color: var(--secondary-color);
}

.footer-logo {
  font-size: 1.6rem;
  margin-bottom: var(--spacing-sm);
  color: var(--primary-blue);
  font-weight: 700;
}

@media (max-width: 1366px) {
  .footer-logo {
    font-size: 1.5rem;
  }
}

@media (max-width: 1024px) {
  .footer-logo {
    font-size: 1.4rem;
  }
}

@media (max-width: 768px) {
  .footer-logo {
    font-size: 1.3rem;
  }
}

.footer-description {
  color: var(--text-light);
  line-height: 1.7;
  margin-bottom: var(--spacing-sm);
  font-size: 0.95rem;
  max-width: 65ch;
}

@media (max-width: 1366px) {
  .footer-description {
    font-size: 0.9rem;
    margin-bottom: 22px;
  }
}

@media (max-width: 1024px) {
  .footer-description {
    margin-bottom: 20px;
  }
}

@media (max-width: 768px) {
  .footer-description {
    font-size: 0.9rem;
    margin-bottom: 18px;
    max-width: 100%;
  }
}

.social-links {
  display: flex;
  gap: 15px;
}

@media (max-width: 768px) {
  .social-links {
    gap: 12px;
  }
}

.social-link {
  width: 44px; /* 符合WCAG 44x44最小触摸目标规范 */
  height: 44px;
  background: color-mix(in srgb, var(--white) 72%, transparent);
  border-radius: var(--rounded-sm); /* 设计系统小圆角 8px */
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-blue);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  overflow: visible; /* 允许二维码弹出层显示 */
  border: 1px solid color-mix(in srgb, var(--white) 85%, transparent);
  box-shadow:
    0 8px 20px color-mix(in srgb, var(--deep-navy) 8%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--white) 90%, transparent);
  position: relative;
  will-change: transform, box-shadow;
  backdrop-filter: blur(12px) saturate(160%);
  -webkit-backdrop-filter: blur(12px) saturate(160%);
}

@media (max-width: 768px) {
  .social-link {
    width: 40px;
    height: 40px;
  }
}

.social-link:hover,
.social-link:focus-visible {
  background: color-mix(in srgb, var(--white) 88%, transparent);
  color: var(--primary-blue);
  transform: translateY(-2px);
  box-shadow:
    0 12px 28px color-mix(in srgb, var(--primary-blue) 18%, transparent),
    inset 0 1px 0 var(--white);
  outline: 2px solid var(--primary-blue);
  outline-offset: 2px;
}

.social-link :deep(svg) {
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
}

.social-link:hover :deep(svg),
.social-link:focus-visible :deep(svg) {
  transform: scale(1.1);
}

.social-image-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.social-link:hover .social-image-icon,
.social-link:focus-visible .social-image-icon {
  transform: scale(1.08);
}

@media (max-width: 768px) {
  .social-link :deep(svg) {
    width: 18px;
    height: 18px;
  }

  .social-image-icon {
    width: 26px;
    height: 26px;
  }
}

/* 社交账号二维码弹出层 */
.social-qrcode {
  position: absolute;
  bottom: calc(100% + 15px);
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: var(--spacing-sm);
  border-radius: var(--rounded-md);
  width: 220px;
  text-align: center;
  z-index: 100;
  pointer-events: none;
  border: 1px solid color-mix(in srgb, var(--white) 60%, transparent);
  background: color-mix(in srgb, var(--white) 85%, transparent); /* 明确背景色，确保文字对比度 */
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

/* hover和focus时显示二维码 */
.social-link:hover .social-qrcode,
.social-link:focus-visible .social-qrcode {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.qrcode-content {
  position: relative;
  z-index: 1;
}

.qrcode-text {
  color: var(--text-dark);
  font-size: 0.85rem;
  font-weight: 500;
  max-width: 180px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: var(--spacing-xs);
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.qrcode-image-wrapper {
  position: relative;
  width: 140px;
  height: 140px;
  margin: 0 auto;
}

.qrcode-image {
  width: 140px;
  height: 140px;
  display: block;
  border-radius: var(--rounded-sm);
  object-fit: contain;
  background: var(--white);
  border: 1px solid color-mix(in srgb, var(--primary-blue) 12%, transparent);
}

.qrcode-center-label {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
  border: 6px solid var(--white);
  border-radius: 8px;
  background: #0a66c2;
  color: var(--white);
  font-family: Arial, Helvetica, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--primary-blue) 12%, transparent);
}

/* 二维码底部小三角指示器 */
.qrcode-arrow {
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: color-mix(in srgb, var(--white) 85%, transparent);
  border-right: 1px solid color-mix(in srgb, var(--white) 60%, transparent);
  border-bottom: 1px solid color-mix(in srgb, var(--white) 60%, transparent);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
}

/* 移动端链接区域横向布局 */
@media (max-width: 768px) {
  .footer-links-section ul {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px 20px;
  }
  
  .footer-links-section ul li {
    white-space: nowrap;
  }
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 30px;
  border-top: 1px solid rgba(15, 39, 64, 0.1);
}

@media (max-width: 1366px) {
  .footer-bottom {
    padding-top: 25px;
  }
}

@media (max-width: 1024px) {
  .footer-bottom {
    padding-top: 20px;
  }
}

@media (max-width: 768px) {
  .footer-bottom {
    flex-direction: column;
    gap: 15px;
    text-align: center;
    padding-top: 20px;
  }
}

.footer-bottom p {
  color: rgba(15, 39, 64, 0.6);
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .footer-bottom p {
    font-size: 0.85rem;
    order: 2;
  }
}

.footer-links {
  display: flex;
  gap: 30px;
}

@media (max-width: 1024px) {
  .footer-links {
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .footer-links {
    gap: 20px;
    order: 1;
    flex-wrap: wrap;
    justify-content: center;
  }
}

.footer-links a {
  color: rgba(15, 39, 64, 0.6);
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

@media (max-width: 768px) {
  .footer-links a {
    font-size: 0.85rem;
  }
}

.footer-links a:hover {
  color: var(--secondary-color);
}

.footer-legal {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px 14px;
  padding-top: 14px;
  flex-wrap: wrap;
  text-align: center;
}

.footer-legal a {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-muted);
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  padding: 6px 12px;
  border-radius: var(--rounded-sm);
  background: color-mix(in srgb, var(--deep-navy) 3%, transparent);
  border: 1px solid transparent;
  will-change: transform, background-color;
}

.footer-legal a:hover,
.footer-legal a:focus-visible {
  color: var(--primary-blue);
  background: color-mix(in srgb, var(--primary-blue) 8%, transparent);
  border-color: color-mix(in srgb, var(--primary-blue) 20%, transparent);
  transform: translateY(-1px);
  outline: none;
}

.legal-divider {
  width: 1px;
  height: 12px;
  background: rgba(15, 39, 64, 0.18);
}

.psb-icon {
  width: 15px;
  height: 17px;
  flex: 0 0 auto;
  object-fit: contain;
}

@media (max-width: 768px) {
  .footer-legal {
    padding-top: 12px;
    gap: 6px 12px;
  }

  .footer-legal a {
    font-size: 0.78rem;
  }

  .legal-divider {
    display: none;
  }

  .social-qrcode {
    width: 160px;
    bottom: calc(100% + 10px);
  }

  .qrcode-text {
    max-width: 120px;
  }

  .qrcode-image-wrapper,
  .qrcode-image {
    width: 120px;
    height: 120px;
  }

  .qrcode-center-label {
    width: 32px;
    height: 32px;
    border-width: 5px;
    border-radius: 7px;
    font-size: 16px;
  }
}

/* 无障碍：减少动效模式支持 */
@media (prefers-reduced-motion: reduce) {
  .social-link,
  .footer-legal a,
  .footer-links a,
  .footer-section ul li a {
    transition: none !important;
  }

  .social-link:hover,
  .social-link:focus-visible,
  .footer-legal a:hover,
  .footer-legal a:focus-visible {
    transform: none !important;
  }

  .social-link:hover :deep(svg),
  .social-link:focus-visible :deep(svg),
  .social-link:hover .social-image-icon,
  .social-link:focus-visible .social-image-icon {
    transform: none !important;
  }
}
</style>
